import { useEffect, useRef, useCallback, useState } from 'react'

/** "接近底部"判定距离（px） */
const NEAR_BOTTOM_THRESHOLD = 200
/** 显示"返回顶部"按钮的滚动距离（px） */
const BACK_TO_TOP_THRESHOLD = 200
/** 流式完成后延迟检查按钮可见性（ms） */
const POST_STREAM_CHECK_DELAY = 100

interface UseScrollControlReturn {
  contentRef: React.RefObject<HTMLDivElement | null>
  showBackToTop: boolean
  showScrollToBottom: boolean
  scrollToTop: () => void
  scrollToBottom: () => void
}

/** 内容区滚动控制 Hook — 管理自动跟随、返回顶部/底部按钮显隐 */
export function useScrollControl(isGenerating: boolean, content: string): UseScrollControlReturn {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const userScrolledUp = useRef(false)
  const scrollListenerAttachedRef = useRef(false)
  const prevIsGenerating = useRef(false)
  const isGeneratingRef = useRef(false)

  // 同步 isGenerating 到 ref，避免 scroll 回调闭包拿到旧值
  useEffect(() => {
    isGeneratingRef.current = isGenerating
  }, [isGenerating])

  const scrollToTop = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [])

  const scrollToBottom = useCallback(() => {
    if (contentRef.current) {
      userScrolledUp.current = false
      setShowScrollToBottom(false)
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [])

  // 新生成开始时重置所有滚动状态
  useEffect(() => {
    if (isGenerating && !prevIsGenerating.current) {
      setShowBackToTop(false)
      setShowScrollToBottom(false)
      userScrolledUp.current = false
    }
    prevIsGenerating.current = isGenerating
  }, [isGenerating])

  const isNearBottom = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD

  // 绑定 scroll 监听（故意不加 deps：每次 render 后更新回调，保证闭包拿到最新 ref 值）—— 用 ref guard 避免重复绑定
  useEffect(() => {
    const el = contentRef.current
    if (!el) {
      scrollListenerAttachedRef.current = false
      return
    }
    if (scrollListenerAttachedRef.current) return

    const handleScroll = () => {
      const st = el.scrollTop

      // 非流式期间：滚动到接近底部时显示"返回顶部"按钮
      if (!isGeneratingRef.current && st > BACK_TO_TOP_THRESHOLD && isNearBottom(el)) {
        setShowBackToTop(true)
      } else if (!isGeneratingRef.current) {
        setShowBackToTop(false)
      }

      // 用户手动向上滚动时停止自动跟随，并显示"回到底部"按钮
      if (!isNearBottom(el)) {
        userScrolledUp.current = true
        if (isGeneratingRef.current) {
          setShowScrollToBottom(true)
        }
      } else if (isGeneratingRef.current) {
        setShowScrollToBottom(false)
      }
    }

    el.addEventListener('scroll', handleScroll, { passive: true })
    scrollListenerAttachedRef.current = true

    return () => {
      el.removeEventListener('scroll', handleScroll)
      scrollListenerAttachedRef.current = false
    }
    // contentRef 稳定，handleScroll 仅读写 ref，无闭包陈旧问题
  }, [])

  // 流式生成时自动滚动到底部（不更新按钮可见性，避免闪烁）
  useEffect(() => {
    if (isGenerating && contentRef.current && !userScrolledUp.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [content, isGenerating])

  // 流式输出完成后延迟检查是否需要显示"返回顶部"按钮
  useEffect(() => {
    if (!isGenerating && contentRef.current) {
      const timer = setTimeout(() => {
        const el = contentRef.current
        if (el && el.scrollHeight > el.clientHeight && el.scrollTop > BACK_TO_TOP_THRESHOLD) {
          setShowBackToTop(true)
        }
      }, POST_STREAM_CHECK_DELAY)
      return () => clearTimeout(timer)
    }
  }, [isGenerating])

  return {
    contentRef,
    showBackToTop,
    showScrollToBottom,
    scrollToTop,
    scrollToBottom,
  }
}
